/**
 * The native shell's handshake.
 *
 * The Android kiosk shell splices a `<script>window.__KIOSK__={…}</script>` into
 * index.html's `<head>` on its way out of the APK, so this is readable
 * synchronously at startup with no race and no polling. On the web deploy it is
 * simply absent, and everything here returns null.
 *
 * It exists mainly to carry a Camera2 census the page cannot obtain for itself.
 * See `src/capture/selectKioskCamera.ts` for why that matters more than it
 * sounds like it should.
 */

export interface KioskShellCamera {
  /** Position in the shell's `CameraManager.getCameraIdList()` walk. */
  index: number;
  cameraId: string;
  facing: 'FRONT' | 'BACK' | 'EXTERNAL' | 'UNKNOWN';
  level: string;
  safe: boolean;
  reason: string | null;
}

/** A resolution the safe camera supports and no other camera does. */
export interface KioskProbeSize {
  width: number;
  height: number;
}

export interface KioskShell {
  shell: 'android';
  shellVersion: string;
  webViewVersion: string;
  cameras: KioskShellCamera[];
  cameraCount: number;
  /** -1 means the shell found nothing safe. Open nothing. */
  safeCameraIndex: number;
  safeCameraLabelHints: string[];
  denyLabelPatterns: string[];
  /**
   * Sizes unique to the safe camera, largest first. Constraining a probe
   * `getUserMedia` to one of these is the only way to unlock Chromium's device
   * list without risking the wrong camera — see `selectKioskCamera.ts`.
   * Empty means no size distinguishes them; open nothing.
   */
  safeCameraProbeSizes: KioskProbeSize[];
  operatorPinSet: boolean;
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

function isProbeSizeArray(v: unknown): v is KioskProbeSize[] {
  return (
    Array.isArray(v) &&
    v.every(
      (x) =>
        !!x &&
        typeof x === 'object' &&
        Number.isFinite((x as KioskProbeSize).width) &&
        Number.isFinite((x as KioskProbeSize).height),
    )
  );
}

/**
 * Validated rather than cast. The shell and the bundle version independently —
 * the APK is reflashed far less often than the web build changes — so a field
 * that moved must read as "no shell" and fail closed, not as `undefined`
 * flowing into a camera constraint.
 */
export function kioskShell(): KioskShell | null {
  const raw = (globalThis as { __KIOSK__?: unknown }).__KIOSK__;
  if (!raw || typeof raw !== 'object') return null;

  const s = raw as Record<string, unknown>;
  if (s.shell !== 'android') return null;
  if (!Array.isArray(s.cameras)) return null;
  if (typeof s.safeCameraIndex !== 'number') return null;
  if (typeof s.cameraCount !== 'number') return null;

  const cameras = s.cameras.filter(
    (c): c is KioskShellCamera =>
      !!c &&
      typeof c === 'object' &&
      typeof (c as KioskShellCamera).index === 'number' &&
      typeof (c as KioskShellCamera).safe === 'boolean',
  );
  if (cameras.length !== s.cameras.length) return null;

  return {
    shell: 'android',
    shellVersion: typeof s.shellVersion === 'string' ? s.shellVersion : 'unknown',
    webViewVersion: typeof s.webViewVersion === 'string' ? s.webViewVersion : 'unknown',
    cameras,
    cameraCount: s.cameraCount,
    safeCameraIndex: s.safeCameraIndex,
    safeCameraLabelHints: isStringArray(s.safeCameraLabelHints) ? s.safeCameraLabelHints : [],
    denyLabelPatterns: isStringArray(s.denyLabelPatterns) ? s.denyLabelPatterns : [],
    // An older APK has no such field. Absent reads as "no probe available",
    // which makes selectKioskCamera refuse — the correct failure for a shell
    // that cannot tell the cameras apart.
    safeCameraProbeSizes: isProbeSizeArray(s.safeCameraProbeSizes) ? s.safeCameraProbeSizes : [],
    operatorPinSet: s.operatorPinSet === true,
  };
}

/** True when running inside the native kiosk shell rather than a browser. */
export function isKioskShell(): boolean {
  return kioskShell() !== null;
}
